/**
 * @fileoverview Slide-out detail view for a single system user.
 * @module system-web/components/UserSlideoutPanel
 *
 * Responsibilities:
 * - Render a system user's profile, system role, invitation lifecycle, and
 *   metadata with Overview / Invitations / Logins / Audit Log tabs.
 * - Provide invitation lifecycle actions (send, resend, revoke, copy link,
 *   refresh status) matching the tenant-web UserDetailPanel.
 * - Provide status actions (suspend, reactivate, soft-delete).
 *
 * Dependencies:
 * - @ui-admin components (AuditLogTimeline, LoginHistoryTab, InlineEditableField)
 * - systemUserService (saveSystemUser, updateSystemUserStatus, invite lifecycle)
 * - systemAuditLogService (fetchAuditLogs), systemRoleService (listSystemRoles)
 *
 * Architecture:
 * - Presentational; all reads/writes flow through the local-first system user
 *   service. Invitation delivery, resend, revoke, and status refresh are the
 *   recognized online provisioning exceptions.
 *
 * Notes:
 * - The local SystemUser shape mirrors UsersPage to avoid a circular import.
 * - Invite tracking layout mirrors tenant-web's Invite Tracking details section.
 */

import React, { useState, useCallback } from 'react';
import {
  Shield, Mail, Clock, User,
  UserX, UserCheck, Trash2, Send,
  RefreshCw, Ban, Copy, Check,
} from 'lucide-react';
import AuditLogTimeline from '@ui-admin/components/AuditLogTimeline';
import { InlineEditableField } from '@ui-admin/components/InlineEditableField';
import LoginHistoryTab from '@ui-admin/components/LoginHistoryTab';
import type { LoginHistoryResult } from '@ui-admin/components/LoginHistoryTab';
import { fetchAuditLogs } from '../services/systemAuditLogService';
import ConfirmActionModal from './ui/ConfirmActionModal';
import { listSystemRoles } from '../services/systemRoleService';
import {
  getSystemUserLoginHistory,
  resendSystemInvitation,
  saveSystemUser,
  updateSystemUserStatus,
  sendSystemUserInvitation,
  revokeSystemUserInvitation,
  refreshSystemUserInviteStatus,
  generateSystemUserInviteLink,
  deriveUserStatus,
} from '../services/systemUserService';
import { useTheme } from '@ui-admin/contexts/ThemeContext';

// ---------------------------------------------------------------------------
// Types (mirrors UsersPage.SystemUser — kept local to avoid circular deps)
// ---------------------------------------------------------------------------

interface InviteLifecycle {
  _id?: string;
  status?: string;
  inviteEmail?: string;
  inviteSentAt?: string;
  invitedAt?: string;
  sentAt?: string;
  expiresAt?: string;
  resendCount?: number;
  lastResentAt?: string;
  acceptedAt?: string;
  revokedAt?: string;
  linkOpenedAt?: string;
  lastLinkOpenedAt?: string;
  linkOpenedCount?: number;
  signupStartedAt?: string;
  completedAt?: string;
  emailDelivery?: { status?: string; lastError?: string; lastEventAt?: string; messageId?: string };
}

interface SystemUser {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  title?: string;
  position?: string;
  status: string;
  systemRole?: { roleId: string; roleName: string } | null;
  departmentMemberships?: { departmentId: string; departmentName: string; roleName: string; status: string }[];
  organizationMemberships?: { organizationId: string; organizationName: string; roleName: string; status: string }[];
  invite?: InviteLifecycle | null;
  invitationHistory?: InviteLifecycle[];
  createdAt?: string;
  lastLoginAt?: string;
  updatedAt?: string;
  entraUserId?: string;
  identities?: any[];
}

interface UserSlideoutPanelProps {
  user: SystemUser;
  onUserUpdated?: () => void;
}

interface SystemRoleOption {
  _id: string;
  name: string;
  status?: string;
}

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  active:      { label: 'Active',              bg: 'bg-green-100',  text: 'text-green-700' },
  suspended:   { label: 'Suspended',           bg: 'bg-yellow-100', text: 'text-yellow-700' },
  invited:     { label: 'Invited',             bg: 'bg-blue-100',   text: 'text-blue-700' },
  not_invited: { label: 'Added - Not Invited', bg: 'bg-gray-100',   text: 'text-gray-600' },
  deleted:     { label: 'Deleted',             bg: 'bg-red-100',    text: 'text-red-700' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function UserSlideoutPanel({ user, onUserUpdated }: UserSlideoutPanelProps) {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<'overview' | 'invitations' | 'audit' | 'logins'>('overview');
  const [systemRoles, setSystemRoles] = useState<SystemRoleOption[]>([]);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [statusChangeTarget, setStatusChangeTarget] = useState<string | null>(null);
  const [inviteActionInFlight, setInviteActionInFlight] = useState<string | null>(null);
  const [inviteFeedback, setInviteFeedback] = useState<{ ok: boolean; message: string } | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  // Clear stale invite feedback when switching users
  React.useEffect(() => {
    setInviteFeedback(null);
    setInviteActionInFlight(null);
    setCopiedLink(false);
  }, [user._id]);

  const derivedStatus = deriveUserStatus(user);
  const statusCfg = STATUS_CONFIG[derivedStatus] || { label: derivedStatus, bg: 'bg-gray-100', text: 'text-gray-600' };

  const statusActionLabel = statusChangeTarget === 'deleted' ? 'soft-delete' : statusChangeTarget === 'suspended' ? 'suspend' : statusChangeTarget === 'active' ? 'reactivate' : '';
  const statusActionVariant = statusChangeTarget === 'deleted' ? 'danger' as const : statusChangeTarget === 'suspended' ? 'warning' as const : 'default' as const;

  const latestInv = user.invite ?? null;
  const historicalInvitations = (user.invitationHistory ?? []).slice().reverse();
  const inviteBusy = inviteActionInFlight !== null;

  const canRefreshInviteStatus = !!latestInv;
  const canResendInvite = !!latestInv && ['pending', 'sent', 'expired'].includes(latestInv.status ?? '');
  const canRevokeInvite = !!latestInv && ['pending', 'sent'].includes(latestInv.status ?? '');
  const canSendNewInvite = !latestInv || ['revoked', 'expired', 'superseded', 'accepted'].includes(latestInv.status ?? '');
  const canCopyInviteLink = !latestInv || ['pending', 'sent', 'expired', 'revoked', 'superseded'].includes(latestInv.status ?? '');

  const TABS: { id: 'overview' | 'invitations' | 'audit' | 'logins'; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'invitations', label: 'Invitations' },
    { id: 'logins', label: 'Logins' },
    { id: 'audit', label: 'Audit Log' },
  ];

  // --- Inline field save ---
  const handleFieldSave = useCallback(async (fieldKey: string, newValue: unknown) => {
    setSavingField(fieldKey);
    try {
      const val = newValue as string;
      const patch: Record<string, any> = {};
      if (fieldKey === 'firstName') patch.firstName = val;
      else if (fieldKey === 'lastName') patch.lastName = val;
      else if (fieldKey === 'title') patch.title = val || undefined;
      else if (fieldKey === 'position') patch.position = val || undefined;
      else if (fieldKey === 'systemRoleId') {
        if (!val) {
          patch.systemRole = null;
        } else {
          let roles = systemRoles;
          if (!roles.length) {
            const fetched = (await listSystemRoles()) as SystemRoleOption[];
            roles = fetched.filter((r) => r.status !== 'inactive');
            setSystemRoles(roles);
          }
          const role = roles.find((r) => r._id === val);
          if (role) {
            patch.systemRole = { roleId: role._id, roleName: role.name };
          }
        }
      }
      await saveSystemUser({ ...user, ...patch });
      onUserUpdated?.();
    } finally {
      setSavingField(null);
    }
  }, [user, systemRoles, onUserUpdated]);

  // --- Status change ---
  const handleStatusChange = useCallback(async () => {
    if (!statusChangeTarget) return;
    try {
      await updateSystemUserStatus(user, statusChangeTarget);
      onUserUpdated?.();
    } finally {
      setStatusChangeTarget(null);
    }
  }, [user, statusChangeTarget, onUserUpdated]);

  // --- Invite lifecycle actions ---
  const handleSendInvite = useCallback(async () => {
    setInviteActionInFlight('send');
    setInviteFeedback(null);
    try {
      await sendSystemUserInvitation(user._id);
      setInviteFeedback({ ok: true, message: 'Invitation sent.' });
      onUserUpdated?.();
    } catch (err) {
      setInviteFeedback({ ok: false, message: err instanceof Error ? err.message : 'Failed to send invitation.' });
    } finally {
      setInviteActionInFlight(null);
    }
  }, [user._id, onUserUpdated]);

  const handleResendInvite = useCallback(async () => {
    setInviteActionInFlight('resend');
    setInviteFeedback(null);
    try {
      const result = await resendSystemInvitation(user);
      if (result.ok) {
        setInviteFeedback({ ok: true, message: 'Invitation resent.' });
        onUserUpdated?.();
      } else {
        setInviteFeedback({ ok: false, message: result.error ?? 'Failed to resend.' });
      }
    } catch (err) {
      setInviteFeedback({ ok: false, message: err instanceof Error ? err.message : 'Failed to resend invitation.' });
    } finally {
      setInviteActionInFlight(null);
    }
  }, [user, onUserUpdated]);

  const handleRevokeInvite = useCallback(async () => {
    setInviteActionInFlight('revoke');
    setInviteFeedback(null);
    try {
      await revokeSystemUserInvitation(user._id);
      setInviteFeedback({ ok: true, message: 'Invitation revoked.' });
      onUserUpdated?.();
    } catch (err) {
      setInviteFeedback({ ok: false, message: err instanceof Error ? err.message : 'Failed to revoke invitation.' });
    } finally {
      setInviteActionInFlight(null);
    }
  }, [user._id, onUserUpdated]);

  const handleRefreshInviteStatus = useCallback(async () => {
    setInviteActionInFlight('refresh');
    try {
      await refreshSystemUserInviteStatus(user._id);
      onUserUpdated?.();
    } catch {
      // swallowed — the service already returns the existing record on failure
    } finally {
      setInviteActionInFlight(null);
    }
  }, [user._id, onUserUpdated]);

  const handleCopyInviteLink = useCallback(async () => {
    setInviteActionInFlight('copy-link');
    setInviteFeedback(null);
    try {
      const result = await generateSystemUserInviteLink(user._id);
      if (result?.inviteUrl) {
        await navigator.clipboard.writeText(result.inviteUrl);
        setCopiedLink(true);
        setInviteFeedback({ ok: true, message: 'Invite link copied to clipboard.' });
        setTimeout(() => setCopiedLink(false), 3000);
      }
      onUserUpdated?.();
    } catch (err) {
      setInviteFeedback({ ok: false, message: err instanceof Error ? err.message : 'Failed to generate invite link.' });
    } finally {
      setInviteActionInFlight(null);
    }
  }, [user._id, onUserUpdated]);

  // --- System role dropdown: eager-load on mount ---
  React.useEffect(() => {
    let active = true;
    listSystemRoles()
      .then((roles) => {
        if (!active) return;
        setSystemRoles((roles as SystemRoleOption[]).filter((r) => r.status !== 'inactive'));
      })
      .catch(() => {
        if (!active) return;
        setSystemRoles([]);
      });
    return () => { active = false; };
  }, []);

  const roleOptions = systemRoles.map((r) => ({ value: r._id, label: r.name }));

  // --- Login history fetcher ---
  const fetchLoginHistory = useCallback(async (
    userId: string,
    opts?: { limit?: number; skip?: number; event?: string },
  ): Promise<LoginHistoryResult> => {
    const result = await getSystemUserLoginHistory(userId, {
      limit: String(opts?.limit ?? 25),
      skip: String(opts?.skip ?? 0),
      ...(opts?.event ? { event: opts.event } : {}),
    });
    return {
      data: result?.data ?? result?.items ?? [],
      total: result?.total ?? 0,
      limit: opts?.limit ?? 25,
      skip: opts?.skip ?? 0,
    };
  }, []);

  // --- Render ---
  return (
    <div className="h-full flex flex-col overflow-hidden text-xs">
      {/* Tab bar */}
      <div className="flex border-b border-gray-200 shrink-0" style={{ background: theme.cardBg } as React.CSSProperties}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-xs font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-b-2 border-orange-500 text-orange-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        {/* --- Overview Tab --- */}
        {activeTab === 'overview' && (
          <div className="space-y-0">
            <Section title="General" icon={User} iconColor="text-gray-400">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                <InlineEditableField
                  label="First Name" value={user.firstName}
                  config={{ fieldKey: 'firstName', editType: 'text', validate: (v) => (v as string)?.trim() ? null : 'Required' }}
                  onSave={handleFieldSave}
                />
                <InlineEditableField
                  label="Last Name" value={user.lastName}
                  config={{ fieldKey: 'lastName', editType: 'text', validate: (v) => (v as string)?.trim() ? null : 'Required' }}
                  onSave={handleFieldSave}
                />
                <InlineEditableField
                  label="Title" value={user.title ?? ''}
                  config={{ fieldKey: 'title', editType: 'text', placeholder: 'Job title' }}
                  onSave={handleFieldSave}
                />
                <InlineEditableField
                  label="Position" value={user.position ?? ''}
                  config={{ fieldKey: 'position', editType: 'text', placeholder: 'Position' }}
                  onSave={handleFieldSave}
                />
                <InlineEditableField
                  label="System Role" value={user.systemRole?.roleName ?? ''}
                  editValue={user.systemRole?.roleId}
                  config={{ fieldKey: 'systemRoleId', editType: 'select', options: roleOptions }}
                  onSave={handleFieldSave}
                />
                <div className="flex items-center gap-2 py-1 md:min-h-[2.5rem]">
                  <span className="text-xs font-medium text-gray-500 w-24 shrink-0">Status</span>
                  <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${statusCfg.bg} ${statusCfg.text}`}>
                    {statusCfg.label}
                  </span>
                </div>
              </div>
            </Section>

            <Section title="Contact" icon={Mail} iconColor="text-blue-400">
              <MetaItem label="Email" value={user.email} />
            </Section>

            <Section title="Metadata" icon={Clock} iconColor="text-purple-400">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <MetaItem label="Created" value={formatDateTime(user.createdAt)} />
                <MetaItem label="Updated" value={formatDateTime(user.updatedAt)} />
                <MetaItem label="Last Login" value={user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never'} />
                {user.entraUserId && <MetaItem label="Entra ID" value={user.entraUserId} />}
              </div>
            </Section>

            {/* Quick invite + status actions */}
            <div className="px-4 py-3 space-y-2 border-t border-gray-100">
              {derivedStatus === 'not_invited' && (
                <button
                  onClick={handleSendInvite}
                  disabled={inviteBusy}
                  className="w-full px-3 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" /> {inviteActionInFlight === 'send' ? 'Sending...' : 'Send Invitation'}
                </button>
              )}
              {derivedStatus === 'invited' && (
                <button
                  onClick={handleResendInvite}
                  disabled={inviteBusy}
                  className="w-full px-3 py-2 text-sm font-medium text-orange-700 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <RefreshCw className="h-4 w-4" /> {inviteActionInFlight === 'resend' ? 'Resending...' : 'Resend Invitation'}
                </button>
              )}
              {user.status === 'active' && (
                <button
                  onClick={() => setStatusChangeTarget('suspended')}
                  className="w-full px-3 py-2 text-sm font-medium text-yellow-700 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors inline-flex items-center justify-center gap-2"
                >
                  <UserX className="h-4 w-4" /> Suspend User
                </button>
              )}
              {user.status === 'suspended' && (
                <button
                  onClick={() => setStatusChangeTarget('active')}
                  className="w-full px-3 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors inline-flex items-center justify-center gap-2"
                >
                  <UserCheck className="h-4 w-4" /> Reactivate User
                </button>
              )}
              {user.status !== 'deleted' && (
                <button
                  onClick={() => setStatusChangeTarget('deleted')}
                  className="w-full px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Trash2 className="h-4 w-4" /> Soft Delete User
                </button>
              )}
              {inviteFeedback && activeTab === 'overview' && (
                <p className={`text-xs ${inviteFeedback.ok ? 'text-green-600' : 'text-red-600'}`}>
                  {inviteFeedback.message}
                </p>
              )}
            </div>
          </div>
        )}

        {/* --- Invitations Tab (mirrors tenant-web) --- */}
        {activeTab === 'invitations' && (
          <div className="px-4 py-3 space-y-4" style={{ background: theme.cardBg } as React.CSSProperties}>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-blue-400" /> Current Invitation
            </h4>

            {!latestInv && (
              <p className="text-xs text-gray-500 italic">No invitation has been sent to this user.</p>
            )}

            {latestInv && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                  <div>
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Status</span>
                    <p className="mt-1 text-sm text-gray-800 capitalize">{latestInv.status ?? 'Unknown'}</p>
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Invited At</span>
                    <p className="mt-1 text-sm text-gray-800">{formatDateTime(latestInv.invitedAt || latestInv.inviteSentAt)}</p>
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Expires At</span>
                    <p className="mt-1 text-sm text-gray-800">{formatDateTime(latestInv.expiresAt)}</p>
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Accepted At</span>
                    <p className="mt-1 text-sm text-gray-800">{latestInv.acceptedAt ? formatDateTime(latestInv.acceptedAt) : 'Not yet accepted'}</p>
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Email Delivery</span>
                    <p className="mt-1 text-sm text-gray-800 capitalize">{latestInv.emailDelivery?.status ?? latestInv.status}</p>
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Resend Count</span>
                    <p className="mt-1 text-sm text-gray-800">{latestInv.resendCount ?? 0}</p>
                  </div>
                </div>

                {/* Invite action buttons */}
                {(canRefreshInviteStatus || canResendInvite || canRevokeInvite || canSendNewInvite || canCopyInviteLink) && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {canRefreshInviteStatus && (
                      <button onClick={handleRefreshInviteStatus} disabled={inviteBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        <RefreshCw className="h-3.5 w-3.5" /> Refresh Status
                      </button>
                    )}
                    {canResendInvite && (
                      <button onClick={handleResendInvite} disabled={inviteBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        <Send className="h-3.5 w-3.5" /> {inviteActionInFlight === 'resend' ? 'Resending...' : 'Resend Invitation'}
                      </button>
                    )}
                    {canCopyInviteLink && (
                      <button onClick={handleCopyInviteLink} disabled={inviteBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        {copiedLink ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {inviteActionInFlight === 'copy-link' ? 'Generating...' : copiedLink ? 'Copied!' : 'Copy Invite Link'}
                      </button>
                    )}
                    {canSendNewInvite && (
                      <button onClick={handleSendInvite} disabled={inviteBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-orange-200 text-orange-700 rounded-lg hover:bg-orange-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        <Send className="h-3.5 w-3.5" /> {inviteActionInFlight === 'send' ? 'Sending...' : 'Send New Invitation'}
                      </button>
                    )}
                    {canRevokeInvite && (
                      <button onClick={handleRevokeInvite} disabled={inviteBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        <Ban className="h-3.5 w-3.5" /> Revoke Invite
                      </button>
                    )}
                  </div>
                )}

                {/* Invite tracking details (mirrors tenant-web) */}
                <details className="rounded-lg border border-gray-200 bg-gray-50">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-3 px-3 py-2 text-xs font-medium text-gray-700">
                    <span>Invite Tracking</span>
                    <span className="text-[10px] text-gray-400">Expand</span>
                  </summary>
                  <div className="px-3 pb-3 space-y-3 border-t border-gray-200">
                    <div className="pt-3 space-y-1.5">
                      {[
                        { label: 'Invited', date: latestInv.invitedAt, done: true },
                        { label: 'Sent', date: latestInv.sentAt ?? latestInv.inviteSentAt, done: !!(latestInv.sentAt ?? latestInv.inviteSentAt) },
                        { label: 'Delivered', date: isDelivered(latestInv) ? latestInv.emailDelivery?.lastEventAt : undefined, done: isDelivered(latestInv) },
                        { label: 'Opened', date: latestInv.linkOpenedAt, done: !!latestInv.linkOpenedAt },
                        { label: 'Accepted', date: latestInv.acceptedAt, done: !!latestInv.acceptedAt },
                      ].map((step) => (
                        <div key={step.label} className="flex items-center gap-2 text-xs">
                          <span className={`w-2 h-2 rounded-full shrink-0 ${step.done ? 'bg-green-500' : 'bg-gray-300'}`} />
                          <span className={`w-32 shrink-0 font-medium ${step.done ? 'text-gray-700' : 'text-gray-400'}`}>{step.label}</span>
                          <span className="text-gray-500">{step.date ? formatDateTime(step.date) : '\u2014'}</span>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                      <div>
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Current Delivery Status</span>
                        <p className="mt-1 text-xs text-gray-700 capitalize">{latestInv.emailDelivery?.status ?? 'No delivery event recorded'}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Last Delivery Event</span>
                        <p className="mt-1 text-xs text-gray-700">{latestInv.emailDelivery?.lastEventAt ? formatDateTime(latestInv.emailDelivery.lastEventAt) : '\u2014'}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Message ID</span>
                        <p className="mt-1 text-xs text-gray-700 break-all">{latestInv.emailDelivery?.messageId ?? '\u2014'}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Last Delivery Error</span>
                        <p className="mt-1 text-xs text-gray-700 break-words">{latestInv.emailDelivery?.lastError ?? '\u2014'}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Current Invite Resends</span>
                        <p className="mt-1 text-xs text-gray-700">{latestInv.resendCount ?? 0}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Prior Invite Attempts</span>
                        <p className="mt-1 text-xs text-gray-700">{historicalInvitations.length}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">First Link Visit</span>
                        <p className="mt-1 text-xs text-gray-700">{latestInv.linkOpenedAt ? formatDateTime(latestInv.linkOpenedAt) : '\u2014'}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Last Link Visit</span>
                        <p className="mt-1 text-xs text-gray-700">{latestInv.lastLinkOpenedAt ? formatDateTime(latestInv.lastLinkOpenedAt) : '\u2014'}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Link Visits</span>
                        <p className="mt-1 text-xs text-gray-700">{latestInv.linkOpenedCount ?? 0}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Signup Started</span>
                        <p className="mt-1 text-xs text-gray-700">{latestInv.signupStartedAt ? formatDateTime(latestInv.signupStartedAt) : '\u2014'}</p>
                      </div>
                    </div>
                  </div>
                </details>

                {/* Historical invitations */}
                {historicalInvitations.length > 0 && (
                  <details className="rounded-lg border border-gray-200 bg-gray-50 mt-2">
                    <summary className="cursor-pointer list-none flex items-center justify-between gap-3 px-3 py-2 text-xs font-medium text-gray-700">
                      <span>Previous Invitations ({historicalInvitations.length})</span>
                      <span className="text-[10px] text-gray-400">Expand</span>
                    </summary>
                    <div className="px-3 pb-3 border-t border-gray-200 space-y-3 pt-3">
                      {historicalInvitations.map((inv, idx) => (
                        <div key={inv._id ?? idx} className="border border-gray-200 rounded-md p-2 bg-white">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            <div>
                              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Status</span>
                              <p className="text-xs text-gray-700 capitalize">{inv.status}</p>
                            </div>
                            <div>
                              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Invited At</span>
                              <p className="text-xs text-gray-700">{formatDateTime(inv.invitedAt || inv.inviteSentAt)}</p>
                            </div>
                            <div>
                              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Delivery</span>
                              <p className="text-xs text-gray-700 capitalize">{inv.emailDelivery?.status ?? inv.status}</p>
                            </div>
                            <div>
                              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Resends</span>
                              <p className="text-xs text-gray-700">{inv.resendCount ?? 0}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </>
            )}

            {/* Send invite for users with no invite */}
            {!latestInv && (
              <button
                onClick={handleSendInvite}
                disabled={inviteBusy}
                className="w-full px-3 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Send className="h-4 w-4" /> {inviteActionInFlight === 'send' ? 'Sending...' : 'Send Invitation'}
              </button>
            )}

            {inviteFeedback && activeTab === 'invitations' && (
              <p className={`text-xs mt-2 ${inviteFeedback.ok ? 'text-green-600' : 'text-red-600'}`}>
                {inviteFeedback.message}
              </p>
            )}
          </div>
        )}

        {/* --- Audit Log Tab --- */}
        {activeTab === 'audit' && (
          <div className="px-4 py-3" style={{ background: theme.cardBg } as React.CSSProperties}>
            <AuditLogTimeline
              documentId={user._id}
              collection="users"
              theme={theme}
              active={activeTab === 'audit'}
              fetchFn={fetchAuditLogs}
            />
          </div>
        )}

        {/* --- Logins Tab --- */}
        {activeTab === 'logins' && (
          <LoginHistoryTab
            userId={user._id}
            fetchFn={fetchLoginHistory}
            active={activeTab === 'logins'}
          />
        )}
      </div>

      {/* Status change confirmation */}
      {statusChangeTarget && (
        <ConfirmActionModal
          title={`${statusActionLabel.charAt(0).toUpperCase() + statusActionLabel.slice(1)} User?`}
          description={<>Are you sure you want to <strong>{statusActionLabel}</strong> <strong>{user.firstName} {user.lastName}</strong>?</>}
          confirmButtonLabel={`Yes, ${statusActionLabel.charAt(0).toUpperCase() + statusActionLabel.slice(1)}`}
          variant={statusActionVariant}
          onConfirm={handleStatusChange}
          onClose={() => setStatusChangeTarget(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Section({ title, icon: Icon, iconColor, children }: {
  title: string;
  icon: React.ElementType;
  iconColor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="px-4 py-3 border-b border-gray-100">
      <h4 className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1.5">
        <Icon className={`h-3.5 w-3.5 ${iconColor}`} />{title}
      </h4>
      {children}
    </div>
  );
}

function formatDateTime(value?: string | null): string {
  if (!value) return '\u2014';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '\u2014' : date.toLocaleString();
}

function isDelivered(inv: InviteLifecycle): boolean {
  const s = inv.emailDelivery?.status;
  return s === 'delivered' || s === 'opened' || s === 'clicked';
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-gray-400">{label}</p>
      <p className="text-gray-700 font-medium">{value}</p>
    </div>
  );
}
